import { useEffect, useState, useContext, useRef } from 'react';
import {
  format,
  isBefore,
  differenceInDays,
  differenceInMonths,
  startOfMonth,
  endOfMonth,
  isSameYear
} from 'date-fns';
import { cn } from '../../lib/utils';
import { DatePicker } from '../ui/DatePicker';
import { MonthPicker } from '../ui/MonthPicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import {
  PresetTimeRange,
  DATE_PRESET_OPTIONS,
  DateBounds,
  calculatePresetTimeRangeBounds,
  earliestPossibleDate,
  latestPossibleDate,
  MAX_RANGE_YEARS,
  maxRangeIsPlural,
  urlKeyReportRange,
  getReportMaxDate
} from '../../utils/owner-portal/presetTimeRange';
import { PreloadContext } from '../preloader/PreloaderProvider';
import {
  dateFromLocalDayEndInBrandTZ,
  dateFromLocalDayStartInBrandTZ,
  fDateInBrandTZ
} from '../../utils/formatTime';

// ----------------------------------------------------------------------

function formatMonthRangeLabel(startDate: Date, endDate: Date): string {
  const sameYear = isSameYear(startDate, endDate);
  const startMonth = format(startDate, 'MMM');
  const endMonth = format(endDate, 'MMM');
  const startYear = format(startDate, 'yyyy');
  const endYear = format(endDate, 'yyyy');

  if (sameYear) {
    return `${startMonth} – ${endMonth}`;
  }
  return `${startMonth} ${startYear} – ${endMonth} ${endYear}`;
}

// ----------------------------------------------------------------------

interface PresetTimeRangePickerProps {
  centered?: boolean;
  defaultRange?: PresetTimeRange;
  filterEndDate: Date | null;
  filterStartDate: Date | null;
  usageLocation?: string;
  onTimeRangeChanged: (dateBounds: DateBounds | null) => void;
  callbackSetDatesLegal?: (legal: boolean) => void;
}

export default function PresetTimeRangePicker({
  centered = false,
  defaultRange = PresetTimeRange.CurrentYear,
  filterEndDate,
  filterStartDate,
  usageLocation,
  onTimeRangeChanged,
  callbackSetDatesLegal
}: PresetTimeRangePickerProps) {
  const [selectedPreset, setSelectedPreset] = useState<PresetTimeRange>(defaultRange);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  const preloadState = useContext(PreloadContext);
  const { earliestSelectableDate } = preloadState;

  // Calculate minimum date
  const minDate = earliestSelectableDate || earliestPossibleDate;

  const useBrandTimezone = usageLocation === 'reservations';
  const isReportsUsage = usageLocation === 'reports';

  // For reports, use month-based max date (18 months from now)
  const maxDate = isReportsUsage ? getReportMaxDate() : latestPossibleDate;

  // Use ref to store latest callback without causing effect re-runs
  const onTimeRangeChangedRef = useRef(onTimeRangeChanged);
  useEffect(() => {
    onTimeRangeChangedRef.current = onTimeRangeChanged;
  }, [onTimeRangeChanged]);

  // Initialize with default range on mount
  useEffect(() => {
    if (defaultRange !== PresetTimeRange.Custom) {
      const bounds = calculatePresetTimeRangeBounds(defaultRange);
      const start = useBrandTimezone ? dateFromLocalDayStartInBrandTZ(bounds.start) : bounds.start;
      const end = useBrandTimezone ? dateFromLocalDayEndInBrandTZ(bounds.end) : bounds.end;
      onTimeRangeChangedRef.current({ start, end });
    }
    // Only run when defaultRange or useBrandTimezone changes, not when callback reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRange, useBrandTimezone]);

  // Validate custom date range
  useEffect(() => {
    if (selectedPreset !== PresetTimeRange.Custom) {
      setDateError(null);
      callbackSetDatesLegal?.(true);
      return;
    }

    if (!customStartDate || !customEndDate) {
      setDateError(null);
      callbackSetDatesLegal?.(true);
      return;
    }

    // Check if end is before start
    if (isBefore(customEndDate, customStartDate)) {
      setDateError(isReportsUsage ? 'End month must be after start month' : 'End date must be after start date');
      callbackSetDatesLegal?.(false);
      return;
    }

    if (isReportsUsage) {
      // For reports, validate month-based range
      const monthsDiff = differenceInMonths(customEndDate, customStartDate);
      if (monthsDiff < 0) {
        setDateError('End month must be after start month');
        callbackSetDatesLegal?.(false);
        return;
      }

      // Report custom ranges use full month bounds
      setDateError(null);
      callbackSetDatesLegal?.(true);
      const start = startOfMonth(customStartDate);
      const end = endOfMonth(customEndDate);
      onTimeRangeChangedRef.current({ start, end });
    } else {
      // Check max range for non-reports
      const daysDiff = differenceInDays(customEndDate, customStartDate);
      const maxDays = MAX_RANGE_YEARS * 365;
      if (daysDiff > maxDays) {
        setDateError(
          `Date range cannot exceed ${MAX_RANGE_YEARS} year${maxRangeIsPlural ? 's' : ''}`
        );
        callbackSetDatesLegal?.(false);
        return;
      }

      setDateError(null);
      callbackSetDatesLegal?.(true);
      const start = useBrandTimezone
        ? dateFromLocalDayStartInBrandTZ(customStartDate)
        : customStartDate;
      const end = useBrandTimezone ? dateFromLocalDayEndInBrandTZ(customEndDate) : customEndDate;
      onTimeRangeChangedRef.current({ start, end });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customStartDate,
    customEndDate,
    selectedPreset,
    callbackSetDatesLegal,
    useBrandTimezone,
    isReportsUsage
  ]);

  const handlePresetChange = (value: string) => {
    const preset = value as PresetTimeRange;
    setSelectedPreset(preset);

    // Update URL param for reports
    if (isReportsUsage) {
      const url = new URL(window.location.href);
      url.searchParams.set(urlKeyReportRange, preset);
      window.history.replaceState({}, '', url.toString());
    }

    if (preset === PresetTimeRange.Custom) {
      if (isReportsUsage) {
        // For reports custom, use current filter dates or default to current month
        const start = filterStartDate ? startOfMonth(filterStartDate) : startOfMonth(new Date());
        const end = filterEndDate ? startOfMonth(filterEndDate) : startOfMonth(new Date());
        setCustomStartDate(start);
        setCustomEndDate(end);
        onTimeRangeChangedRef.current({ start: startOfMonth(start), end: endOfMonth(end) });
      } else {
        // For non-reports custom, use current filter dates or defaults
        const start = filterStartDate || new Date();
        const end = filterEndDate || new Date();
        setCustomStartDate(start);
        setCustomEndDate(end);
        const startOut = useBrandTimezone ? dateFromLocalDayStartInBrandTZ(start) : start;
        const endOut = useBrandTimezone ? dateFromLocalDayEndInBrandTZ(end) : end;
        onTimeRangeChangedRef.current({ start: startOut, end: endOut });
      }
    } else {
      const bounds = calculatePresetTimeRangeBounds(preset);
      const start = useBrandTimezone ? dateFromLocalDayStartInBrandTZ(bounds.start) : bounds.start;
      const end = useBrandTimezone ? dateFromLocalDayEndInBrandTZ(bounds.end) : bounds.end;
      onTimeRangeChangedRef.current({ start, end });
    }
  };

  const isCustomMode = selectedPreset === PresetTimeRange.Custom;

  return (
    <div className={cn('flex flex-col gap-4 w-full min-w-0', centered && 'items-center')}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-4 w-full list-none sm:flex-nowrap min-w-0',
          centered && 'justify-center'
        )}
      >
        <div className="w-full sm:w-auto min-w-0">
          <Select value={selectedPreset} onValueChange={handlePresetChange}>
            <SelectTrigger className="h-9 w-full sm:w-[150px] shrink-0 border-input bg-background">
              <SelectValue placeholder="Select range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESET_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isCustomMode ? (
          isReportsUsage ? (
            <div className="flex flex-nowrap items-center gap-4 min-w-0 w-full flex-1 sm:flex-initial px-0 max-w-sm">
              <MonthPicker
                value={customStartDate}
                onChange={(date) => setCustomStartDate(date || null)}
                placeholder="Start month"
                minDate={minDate}
                maxDate={maxDate}
                className="min-w-0 flex-1 basis-0"
                error={!!dateError}
                displayFormat="MMM yyyy"
              />
              <span
                className="text-muted-foreground text-sm shrink-0 flex justify-center items-center [list-style:none] before:content-none after:content-none"
                style={{ listStyle: 'none' }}
              >
                to
              </span>
              <MonthPicker
                value={customEndDate}
                onChange={(date) => setCustomEndDate(date || null)}
                placeholder="End month"
                minDate={customStartDate || minDate}
                maxDate={maxDate}
                className="min-w-0 flex-1 basis-0"
                error={!!dateError}
                displayFormat="MMM yyyy"
              />
            </div>
          ) : (
            <div className="flex flex-nowrap items-center gap-4 min-w-0 w-full flex-1 sm:flex-initial px-0 max-w-sm">
              <DatePicker
                value={customStartDate}
                onChange={(date) => setCustomStartDate(date || null)}
                placeholder="Start date"
                minDate={minDate}
                maxDate={latestPossibleDate}
                className="min-w-0 flex-1 basis-0"
                error={!!dateError}
                displayFormat="MMM d, yyyy"
              />
              <span
                className="text-muted-foreground text-sm shrink-0 flex justify-center items-center [list-style:none] before:content-none after:content-none"
                style={{ listStyle: 'none' }}
              >
                to
              </span>
              <DatePicker
                value={customEndDate}
                onChange={(date) => setCustomEndDate(date || null)}
                placeholder="End date"
                minDate={customStartDate || minDate}
                maxDate={latestPossibleDate}
                className="min-w-0 flex-1 basis-0"
                error={!!dateError}
                displayFormat="MMM d, yyyy"
              />
            </div>
          )
        ) : (
          filterStartDate &&
          filterEndDate && (
            <span className="inline-flex items-center rounded-md border border-border/60 bg-muted/40 px-2.5 py-1 text-sm text-muted-foreground tabular-nums">
              {isReportsUsage
                ? formatMonthRangeLabel(filterStartDate, filterEndDate)
                : useBrandTimezone
                  ? `${fDateInBrandTZ(filterStartDate, 'MMM D, YYYY')} – ${fDateInBrandTZ(
                      filterEndDate,
                      'MMM D, YYYY'
                    )}`
                  : `${format(filterStartDate, 'MMM d, yyyy')} – ${format(
                      filterEndDate,
                      'MMM d, yyyy'
                    )}`}
            </span>
          )
        )}
      </div>

      {dateError && <p className="text-sm text-destructive">{dateError}</p>}
    </div>
  );
}
